import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronsUpDown, Plus, RefreshCw, Settings, UserCog } from 'lucide-react';

import { BrandLogo } from '@/components/BrandLogo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { SettingsDialog } from '@/features/settings/SettingsDialog';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { AccountRow } from '@/features/accounts/AccountRow';
import { AddAccountDialog } from '@/features/accounts/AddAccountDialog';
import { AllAccountsRow } from '@/features/accounts/AllAccountsRow';
import { ArchivedAccountsSection } from '@/features/accounts/ArchivedAccountsSection';
import { BanAttentionBanner } from '@/features/accounts/BanAttentionBanner';
import { QrAttentionBanner } from '@/features/accounts/QrAttentionBanner';
import { ACCOUNT_COLOR_PALETTE, type AccountColor } from '@/features/accounts/account-colors';
import { archivedAccounts, visibleAccounts } from '@/features/accounts/accounts.api';
import { useAccountColors } from '@/features/accounts/useAccountColors';
import { useAccounts } from '@/features/accounts/useAccounts';
import { ManageAgentsDialog } from '@/features/agents/ManageAgentsDialog';
import { useAuthStore } from '@/store/auth';

/**
 * Left column of the console.
 *
 * Stacks (top -> bottom): brand strip, QR attention banner (if any account
 * needs re-scan), scrollable account list, agent identity dropdown.
 *
 * The component is self-contained: it owns its own padding and divider so
 * the Layout grid cell can simply mount `<AccountSidebar />` without any
 * surrounding wrapper.
 */
export function AccountSidebar(): React.JSX.Element {
  const query = useAccounts();
  // Computed once here (rather than inside each `AccountRow`) and threaded
  // down as a prop — every row calling the hook itself would mount its own
  // query observer, which is needless churn on top of this component's own
  // already-cache-warm `useAccounts()` subscription above.
  const accountColors = useAccountColors();
  const firstQrRowRef = useRef<HTMLDivElement | null>(null);
  const firstBanRowRef = useRef<HTMLDivElement | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);

  // Gate: only admins may add new accounts.
  const role = useAuthStore((s) => s.agent?.role);
  const isAdmin = role === 'admin';

  // Hidden (archived) accounts never count for the attention banners — they
  // are out of the pool (see `visibleAccounts`).
  const qrRequiredCount = useMemo(() => {
    return visibleAccounts(query.data ?? []).filter((a) => a.status === 'qr_required').length;
  }, [query.data]);

  const bannedCount = useMemo(() => {
    return visibleAccounts(query.data ?? []).filter((a) => a.status === 'banned').length;
  }, [query.data]);

  function scrollToFirstQrAccount(): void {
    firstQrRowRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
    firstQrRowRef.current?.focus();
  }

  function scrollToFirstBanAccount(): void {
    firstBanRowRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
    firstBanRowRef.current?.focus();
  }

  return (
    <aside className="neu-panel-left relative z-20 flex h-full flex-col shadow-neu-rail">
      {/* Brand strip — green gradient logo tile + wordmark (design §左栏).
          The tile+wordmark now come from the shared `BrandLogo` so the exact
          same mark can also render in the conversation-column header (where it
          keeps the console branded while this rail is collapsed) without the
          markup being duplicated. `flex-1` keeps the wordmark stretching so
          the add-account button stays pinned to the right edge, as before. */}
      <header className="flex shrink-0 items-center gap-3 px-4 pb-2 pt-5">
        <BrandLogo className="flex-1" />
        {isAdmin && (
          <Button
            type="button"
            size="icon"
            variant="ghost"
            aria-label="管理坐席"
            className="size-8 shrink-0 rounded-[10px] shadow-neu-raised-sm hover:shadow-neu-raised active:shadow-neu-inset-press"
            onClick={() => setManageOpen(true)}
          >
            <UserCog className="size-4" />
          </Button>
        )}
        {isAdmin && (
          <Button
            type="button"
            size="icon"
            variant="ghost"
            aria-label="添加账号"
            className="size-8 shrink-0 rounded-[10px] shadow-neu-raised-sm hover:shadow-neu-raised active:shadow-neu-inset-press"
            onClick={() => setAddDialogOpen(true)}
          >
            <Plus className="size-4" />
          </Button>
        )}
      </header>
      {/* AddAccountDialog — only mounted when the admin opens it. */}
      {isAdmin && (
        <AddAccountDialog
          open={addDialogOpen}
          onOpenChange={setAddDialogOpen}
        />
      )}
      {/* ManageAgentsDialog — admin-gated entry point lives in this header's
          icon button; only mounted when the admin opens it. */}
      {isAdmin && (
        <ManageAgentsDialog
          open={manageOpen}
          onOpenChange={setManageOpen}
        />
      )}

      {/* Attention banners + scrollable account list share a flex column so a
          banner stays sticky to the top of the list while the list itself
          scrolls. The ban banner is rendered ABOVE the QR banner: a ban is the
          more severe state (and, unlike qr_required, unrecoverable by re-scan),
          so it takes visual precedence when both happen to be present. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <BanAttentionBanner
          count={bannedCount}
          onClick={scrollToFirstBanAccount}
        />
        <QrAttentionBanner
          count={qrRequiredCount}
          onClick={scrollToFirstQrAccount}
        />
        <AccountList
          query={query}
          firstQrRowRef={firstQrRowRef}
          firstBanRowRef={firstBanRowRef}
          colorMap={accountColors}
          isAdmin={isAdmin}
        />
      </div>

      {/* Agent identity + sign-out — bottom-anchored. */}
      <AgentIdentityFooter />
    </aside>
  );
}

// AccountList --------------------------------------------------------------

interface AccountListProps {
  query: ReturnType<typeof useAccounts>;
  firstQrRowRef: React.MutableRefObject<HTMLDivElement | null>;
  firstBanRowRef: React.MutableRefObject<HTMLDivElement | null>;
  colorMap: Map<string, AccountColor>;
  /** Admins also get the collapsed 「已隐藏 (N)」 section at the bottom. */
  isAdmin: boolean;
}

function AccountList({
  query,
  firstQrRowRef,
  firstBanRowRef,
  colorMap,
  isAdmin,
}: AccountListProps): React.JSX.Element {
  // The pinned "所有账号" row is the list's literal first `<li>`, always —
  // it never waits on (or reacts to) the accounts query's loading / error /
  // empty state, since selecting it doesn't depend on the accounts list at
  // all. `AccountListBody` below renders whatever the accounts query state
  // calls for as the remaining `<li>`s of this SAME `<ul>`, so the pinned
  // row reads as part of one continuous list rather than a separate section.
  // It also gets NO color swatch — see `AllAccountsRow`'s own doc comment.
  return (
    <ul className="flex flex-col gap-2.5 px-4 py-2">
      <li>
        <AllAccountsRow />
      </li>
      <AccountListBody
        query={query}
        firstQrRowRef={firstQrRowRef}
        firstBanRowRef={firstBanRowRef}
        colorMap={colorMap}
        isAdmin={isAdmin}
      />
      {isAdmin && query.data && (
        <ArchivedAccountsSection accounts={archivedAccounts(query.data)} />
      )}
    </ul>
  );
}

function AccountListBody({
  query,
  firstQrRowRef,
  firstBanRowRef,
  colorMap,
}: AccountListProps): React.JSX.Element {
  // Loading — three skeleton rows. Use the same padding/gap as a real row so
  // the height doesn't jump when data arrives.
  if (query.isLoading) {
    return (
      <>
        {[0, 1, 2].map((i) => (
          <li key={i} className="flex items-center gap-3 px-3 py-2">
            <Skeleton className="size-2.5 rounded-full" />
            <Skeleton className="h-4 flex-1" />
          </li>
        ))}
      </>
    );
  }

  // Error — quiet message + retry button. Don't surface the raw axios error.
  if (query.isError) {
    return (
      <li className="flex flex-col items-start gap-2 px-4 py-4 text-sm text-muted-foreground">
        <p>Failed to load accounts</p>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => query.refetch()}
        >
          <RefreshCw className="mr-2 size-3.5" />
          Retry
        </Button>
      </li>
    );
  }

  // The POOL: hidden (archived) rows are listed by the hub — old
  // conversations are labelled with them — but never shown here (admins
  // find them in the 「已隐藏」 section below the list).
  const accounts = visibleAccounts(query.data ?? []);

  // Empty — no accounts configured. Calm copy; M3 ops sets up accounts via
  // the gateway, not the console.
  if (accounts.length === 0) {
    return (
      <li className="flex flex-col gap-1 px-4 py-6 text-sm text-muted-foreground">
        <p className="font-medium text-foreground">
          No WhatsApp accounts configured yet
        </p>
        <p className="text-xs">
          Connect an account via the gateway, then it will appear here.
        </p>
      </li>
    );
  }

  // Track the FIRST qr_required and the FIRST banned row so each attention
  // banner can scroll to its target. A row's status is exactly one value, so
  // no row is ever both the first-qr and the first-ban anchor.
  let qrSeen = false;
  let banSeen = false;

  return (
    <>
      {accounts.map((account) => {
        const isFirstQr = account.status === 'qr_required' && !qrSeen;
        if (isFirstQr) {
          qrSeen = true;
        }
        const isFirstBan = account.status === 'banned' && !banSeen;
        if (isFirstBan) {
          banSeen = true;
        }
        // Falls back to the palette's first entry rather than leaving the
        // swatch unstyled — in practice this account's id is always in
        // `colorMap`, since it's built from this same `accounts` list.
        const color = colorMap.get(account.id) ?? ACCOUNT_COLOR_PALETTE[0];
        return (
          <li key={account.id}>
            <AccountRow
              ref={isFirstQr ? firstQrRowRef : isFirstBan ? firstBanRowRef : undefined}
              account={account}
              color={color}
            />
          </li>
        );
      })}
    </>
  );
}

// AgentIdentityFooter ------------------------------------------------------

function AgentIdentityFooter(): React.JSX.Element | null {
  const agent = useAuthStore((s) => s.agent);
  const clear = useAuthStore((s) => s.clear);
  const navigate = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);

  if (!agent) {
    // RequireAuth holds rendering of the Layout until /auth/me resolves, so
    // we should always have an agent here — but defensively bail rather than
    // crash the sidebar if something upstream misfires.
    return null;
  }

  const initials = computeInitials(agent.display_name, agent.email);

  function handleSignOut(): void {
    clear();
    // Hard navigate path goes through RequireAuth; the explicit navigate
    // matches the LoginPage post-login flow.
    navigate('/login', { replace: true });
  }

  return (
    // Bottom agent card — raised (design §左栏 底部坐席卡). Same DOM shape
    // as before; only the chrome changed (card instead of a bordered strip).
    <div className="m-3 mt-1 flex shrink-0 items-center gap-1 rounded-[14px] p-2 shadow-neu-raised">
      <ThemeToggle />
      {/* Settings gear — opens the neu Settings modal (theme + prefs).
          Styled to sit beside the ThemeToggle (same size / muted-ghost look)
          with neu press feedback. */}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={() => setSettingsOpen(true)}
        aria-label="设置"
        className="neu-pressable size-8 shrink-0 text-muted-foreground hover:text-foreground active:shadow-neu-inset-press"
      >
        <Settings className="size-4" aria-hidden="true" />
      </Button>
      {settingsOpen && (
        <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
      )}
      <div className="flex-1 min-w-0">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            data-testid="agent-dropdown-trigger"
            className="group flex w-full cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {/* Avatar carries the trigger's press feedback: hovering ANYWHERE
                on the trigger row raises it, pressing sinks it (group-*), so
                the whole click target reads as one button. */}
            <Avatar className="neu-pressable size-9 shadow-neu-flat group-hover:shadow-neu-raised group-active:shadow-neu-inset-press">
              <AvatarFallback className="neu-avatar-agent font-extrabold text-white">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-extrabold">{agent.display_name}</p>
              <p className="truncate text-xs capitalize text-muted-foreground">
                {agent.role}
              </p>
            </div>
            <ChevronsUpDown
              className="size-4 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          side="top"
          className="w-[--radix-dropdown-menu-trigger-width] min-w-[12rem]"
        >
          <DropdownMenuItem onSelect={handleSignOut}>Sign out</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      </div>
    </div>
  );
}

/**
 * Two-letter initials from the display name (preferred) or the email local
 * part (fallback). "Ops Dev" -> "OD"; "horus@example.com" -> "HO".
 */
function computeInitials(displayName: string, email: string): string {
  const source = displayName.trim() || email.split('@')[0] || '?';
  const tokens = source.split(/\s+/).filter(Boolean);
  if (tokens.length >= 2) {
    return (tokens[0][0] + tokens[1][0]).toUpperCase();
  }
  const single = tokens[0] ?? '?';
  return single.slice(0, 2).toUpperCase();
}
